import { Toast } from "@base-ui/react/toast";
import { CircleAlertIcon, CircleCheckIcon, InfoIcon, LoaderCircleIcon, TriangleAlertIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { buttonVariants } from "@/components/ui/button";
import { Z_INDEX_CLASS_NAMES } from "@/configs/z-index";
import { NAMESPACES } from "@/i18n/constants";
import { ICON_KEYS, iconRegistry } from "@/icons";
import { cn } from "@/utils/class-name";

const toastManager = Toast.createToastManager();

const CloseIcon = iconRegistry[ICON_KEYS.CLOSE];

const TOAST_ICONS = {
    loading: LoaderCircleIcon,
    success: CircleCheckIcon,
    error: CircleAlertIcon,
    info: InfoIcon,
    warning: TriangleAlertIcon,
} as const;

type ToastPosition = "top-left" | "top-center" | "top-right" | "bottom-left" | "bottom-center" | "bottom-right";

interface ToastProviderProps extends Toast.Provider.Props {
    position?: ToastPosition;
}

function ToastProvider({ children, position = "bottom-right", ...props }: ToastProviderProps) {
    return (
        <Toast.Provider toastManager={toastManager} {...props}>
            {children}
            <ToastList position={position} />
        </Toast.Provider>
    );
}

function ToastList({ position = "bottom-right" }: { position: ToastPosition }) {
    const { toasts } = Toast.useToastManager();
    const { t: tCommon } = useTranslation(NAMESPACES.COMMON);
    const isTop = position.startsWith("top");

    return (
        <Toast.Portal data-slot={"toast-portal"}>
            <Toast.Viewport
                className={cn(
                    Z_INDEX_CLASS_NAMES.TOAST,
                    "fixed mx-auto flex w-[calc(100%-var(--toast-inset)*2)] max-w-90 [--toast-inset:--spacing(4)] sm:[--toast-inset:--spacing(8)]",
                    // Vertical positioning
                    "data-[position*=top]:top-(--toast-inset)",
                    "data-[position*=bottom]:bottom-(--toast-inset)",
                    // Horizontal positioning
                    "data-[position*=left]:left-(--toast-inset)",
                    "data-[position*=right]:right-(--toast-inset)",
                    "data-[position*=center]:left-1/2 data-[position*=center]:-translate-x-1/2",
                )}
                data-slot={"toast-viewport"}
                data-position={position}
            >
                {toasts.map((toast) => {
                    const Icon = toast.type ? TOAST_ICONS[toast.type as keyof typeof TOAST_ICONS] : null;

                    const getSwipeDirection: () => ("left" | "right" | "up" | "down")[] = () => {
                        const verticalDirection = isTop ? "up" : "down";
                        if (position.includes("center")) {
                            return [verticalDirection];
                        }
                        if (position.includes("left")) {
                            return ["left", verticalDirection];
                        }
                        return ["right", verticalDirection];
                    };

                    return (
                        <Toast.Root
                            key={toast.id}
                            toast={toast}
                            data-position={position}
                            swipeDirection={getSwipeDirection()}
                            className={cn(
                                "absolute z-[calc(9999-var(--toast-index))] h-(--toast-calc-height) w-full rounded-lg border bg-popover bg-clip-padding px-3.5 py-3 text-popover-foreground shadow-lg select-none [transition:transform_.5s_cubic-bezier(.22,1,.36,1),opacity_.5s,height_.15s] before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-lg)-1px)] before:shadow-[0_1px_--theme(--color-black/4%)] dark:bg-clip-border dark:before:shadow-[0_-1px_--theme(--color-white/8%)]",
                                // Base positioning using data-position
                                "data-[position*=right]:right-0 data-[position*=right]:left-auto",
                                "data-[position*=left]:right-auto data-[position*=left]:left-0",
                                "data-[position*=center]:right-0 data-[position*=center]:left-0",
                                "data-[position*=top]:top-0 data-[position*=top]:bottom-auto data-[position*=top]:origin-top",
                                "data-[position*=bottom]:top-auto data-[position*=bottom]:bottom-0 data-[position*=bottom]:origin-bottom",
                                // Gap fill for hover
                                "after:absolute after:left-0 after:h-[calc(var(--toast-gap)+1px)] after:w-full",
                                "data-[position*=top]:after:top-full",
                                "data-[position*=bottom]:after:bottom-full",
                                // Define some variables
                                "[--toast-calc-height:var(--toast-frontmost-height,var(--toast-height))] [--toast-gap:--spacing(3)] [--toast-peek:--spacing(3)] [--toast-scale:calc(max(0,1-(var(--toast-index)*.1)))] [--toast-shrink:calc(1-var(--toast-scale))]",
                                // Define offset-y variable
                                "data-[position*=top]:[--toast-calc-offset-y:calc(var(--toast-offset-y)+var(--toast-index)*var(--toast-gap)+var(--toast-swipe-movement-y))]",
                                "data-[position*=bottom]:[--toast-calc-offset-y:calc(var(--toast-offset-y)*-1+var(--toast-index)*var(--toast-gap)*-1+var(--toast-swipe-movement-y))]",
                                // Default state transform
                                "data-[position*=top]:[transform:translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)+(var(--toast-index)*var(--toast-peek))+(var(--toast-shrink)*var(--toast-calc-height))))_scale(var(--toast-scale))]",
                                "data-[position*=bottom]:[transform:translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)-(var(--toast-index)*var(--toast-peek))-(var(--toast-shrink)*var(--toast-calc-height))))_scale(var(--toast-scale))]",
                                // Limited state
                                "data-limited:opacity-0",
                                // Expanded state
                                "data-expanded:h-(--toast-height)",
                                "data-[position]:data-expanded:[transform:translateX(var(--toast-swipe-movement-x))_translateY(var(--toast-calc-offset-y))]",
                                // Starting and ending animations
                                "data-[position*=top]:data-starting-style:[transform:translateY(calc(-100%-var(--toast-inset)))]",
                                "data-[position*=bottom]:data-starting-style:[transform:translateY(calc(100%+var(--toast-inset)))]",
                                "data-ending-style:opacity-0",
                                // Ending animations (direction-aware)
                                "data-ending-style:not-data-limited:not-data-swipe-direction:[transform:translateY(calc(100%+var(--toast-inset)))]",
                                "data-ending-style:data-[swipe-direction=left]:[transform:translateX(calc(var(--toast-swipe-movement-x)-100%-var(--toast-inset)))_translateY(var(--toast-calc-offset-y))]",
                                "data-ending-style:data-[swipe-direction=right]:[transform:translateX(calc(var(--toast-swipe-movement-x)+100%+var(--toast-inset)))_translateY(var(--toast-calc-offset-y))]",
                                "data-ending-style:data-[swipe-direction=up]:[transform:translateY(calc(var(--toast-swipe-movement-y)-100%-var(--toast-inset)))]",
                                "data-ending-style:data-[swipe-direction=down]:[transform:translateY(calc(var(--toast-swipe-movement-y)+100%+var(--toast-inset)))]",
                                // Ending animations (expanded)
                                "data-expanded:data-ending-style:data-[swipe-direction=left]:[transform:translateX(calc(var(--toast-swipe-movement-x)-100%-var(--toast-inset)))_translateY(var(--toast-calc-offset-y))]",
                                "data-expanded:data-ending-style:data-[swipe-direction=right]:[transform:translateX(calc(var(--toast-swipe-movement-x)+100%+var(--toast-inset)))_translateY(var(--toast-calc-offset-y))]",
                                "data-expanded:data-ending-style:data-[swipe-direction=up]:[transform:translateY(calc(var(--toast-swipe-movement-y)-100%-var(--toast-inset)))]",
                                "data-expanded:data-ending-style:data-[swipe-direction=down]:[transform:translateY(calc(var(--toast-swipe-movement-y)+100%+var(--toast-inset)))]",
                            )}
                        >
                            <Toast.Content
                                className={
                                    "flex items-center justify-between gap-1.5 overflow-hidden text-sm transition-opacity duration-250 data-behind:pointer-events-none data-behind:opacity-0 data-expanded:pointer-events-auto data-expanded:opacity-100"
                                }
                            >
                                <div className={"flex gap-2"}>
                                    {Icon && (
                                        <div
                                            className={
                                                "mt-.5 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&>svg]:h-[1lh] [&>svg]:w-4"
                                            }
                                            data-slot={"toast-icon"}
                                        >
                                            <Icon
                                                className={
                                                    "in-data-[type=error]:text-destructive in-data-[type=info]:text-info in-data-[type=loading]:animate-spin in-data-[type=loading]:opacity-72 in-data-[type=success]:text-success in-data-[type=warning]:text-warning"
                                                }
                                            />
                                        </div>
                                    )}

                                    {/* `select-text` here so the message can be copied. */}
                                    <div className={"flex flex-col gap-0.5 select-text"}>
                                        <Toast.Title className={"font-medium"} data-slot={"toast-title"} />
                                        <Toast.Description
                                            className={"text-muted-foreground"}
                                            data-slot={"toast-description"}
                                        />
                                    </div>
                                </div>
                                <div className={"flex shrink-0 items-start gap-1.5"}>
                                    {toast.actionProps && (
                                        <Toast.Action
                                            className={buttonVariants({ size: "xs" })}
                                            data-slot={"toast-action"}
                                        >
                                            {toast.actionProps.children}
                                        </Toast.Action>
                                    )}
                                    <Toast.Close
                                        aria-label={tCommon("states.close")}
                                        className={cn(
                                            buttonVariants({ variant: "ghost", size: "icon-sm" }),
                                            "-me-1 -mt-1 size-6 text-muted-foreground",
                                        )}
                                        data-slot={"toast-close"}
                                    >
                                        <CloseIcon aria-hidden={"true"} className={"size-3.5"} />
                                    </Toast.Close>
                                </div>
                            </Toast.Content>
                        </Toast.Root>
                    );
                })}
            </Toast.Viewport>
        </Toast.Portal>
    );
}

export { ToastProvider, type ToastPosition, toastManager };
